import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { ToastModal } from 'design-system/ToastModal';
import Text from 'design-system/Text';
import { tierThemes } from 'features/premium/benefits';
import { Tier } from 'features/premium/entities';
import { selectUserTier } from 'features/premium/selectors/tier';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import Modal from 'utils/packages/Modal';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { IconClose, rem } from 'design-system';
import createStyleSheets from 'utils/createStyleSheets';
import { useTimeout } from 'hooks/timers';
import TouchableBounce from 'utils/packages/TouchableBounce';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectSpaceInfo } from '../reducers/selectors';
import { isWeb } from '../../../constants';
const useMoveSpaceModal = () => {
    const navigation = useMainStackNavigation();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const [isToastVisible, setIsToastVisible] = useState(false);
    const spaceInfoRecord = useAppSelector(selectSpaceInfo);
    const userTier = useAppSelector(selectUserTier);
    const tierTheme = tierThemes[userTier];
    const showMoveSpaceToast = useCallback(() => {
        setIsToastVisible(true);
    }, []);
    useTimeout(() => setIsToastVisible(false), isToastVisible ? 4000 : undefined);
    const onCloseToast = useCallback(() => {
        setIsToastVisible(false);
    }, []);
    const { width } = useAppFrameDimensions(isWeb);
    const moveAccountsToast = useCallback(() => (<ToastModal isVisible={isToastVisible} position="bottom" style={[styles.modal, { width }]} onCloseAction={onCloseToast}>
        <View style={styles.toastTextContainer}>
          <Text TextThin-16 White>
            All your previous transaction edits and custom categories are now in
            the new Space
          </Text>
        </View>
        <TouchableBounce style={styles.closeIcon} onPress={onCloseToast}>
          <IconClose alt color={colors.elements.white}/>
        </TouchableBounce>
      </ToastModal>), [
        isToastVisible,
        styles.modal,
        styles.toastTextContainer,
        styles.closeIcon,
        width,
        onCloseToast,
        colors.elements.white,
    ]);
    const showMoveSpaceUpgradeModal = useCallback((onContinue) => {
        Modal.showConfirmation({
            title: 'Do you want to keep your transaction edits?',
            subtitle: `You're moving accounts into your Private space, which is on the ${tierTheme.name} plan. Some transaction edits may not carry over, and custom categories may switch back to the defaults.\n\nPlease upgrade your plan to ${tierThemes[Tier.pro].shortName} or ${tierThemes[Tier.ultimate].shortName} if you wish to keep the edits.`,
            cancelButtonTitle: 'Yes, let me upgrade',
            confirmButtonTitle: 'Continue and lose edits',
            onPressCancel: () => {
                navigation.navigateDeprecated('EmmaProModal', {
                    tier: Tier.pro,
                    onUpgradeCallback: onContinue,
                });
            },
            onConfirm: () => {
                Modal.hide();
                onContinue();
            },
        });
    }, [tierTheme.name]);
    const isSameTierSpaceMovement = useCallback((fromSpaceId, toSpaceId) => {
        if (!fromSpaceId)
            return null;
        const fromSpace = spaceInfoRecord[fromSpaceId]?.space;
        const toSpace = spaceInfoRecord[toSpaceId]?.space;
        if (!fromSpace || !toSpace)
            return null;
        if (fromSpace.isPremium && toSpace.isPremium) {
            return true;
        }
        if (fromSpace.isPremium && !toSpace.isPremium) {
            return false;
        }
        return null;
    }, [spaceInfoRecord]);
    return {
        showMoveSpaceUpgradeModal,
        moveAccountsToast,
        showMoveSpaceToast,
        isSameTierSpaceMovement,
    };
};
const styleSet = createStyleSheets((colors) => ({
    modal: {
        marginBottom: rem(100),
    },
    iconEmma: {
        marginRight: rem(12),
    },
    closeIcon: {
        marginLeft: rem(12),
        alignSelf: 'flex-start',
        backgroundColor: colors.background.transparent,
        borderRadius: rem(8),
        padding: rem(4),
    },
    toastTextContainer: {
        flex: 1,
    },
}));
export default useMoveSpaceModal;
